/* eslint-disable @typescript-eslint/prefer-destructuring, @typescript-eslint/no-unnecessary-condition */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { ProjectsAPI } from '@newo/api/gateway';
import type {
    TProject,
    TProjectData,
    TProjectImageData,
    TProjectPublishData,
    TSearchResult,
    TV3CreateProjectData,
    TV3Project,
    TV3UpdateProjectData,
    TLocalCustomerLinkPayload,
    TRegistryCustomerLinkPayload
} from '@newo/api/gateway';
import axios from '@/utils/api/axiosInstance';
import type { MutationOptions } from '@/types/common';
import { designerV2API } from '@/services/designer/designerV2Client';

export const projectsAPI = new ProjectsAPI(axios);

export const PROJECT_QUERY_KEYS = {
    detail: (isProjectsV2Enabled: boolean, projectId?: string) =>
        ['project', isProjectsV2Enabled, projectId] as const,
    list: (isProjectsV2Enabled: boolean) =>
        ['projects', isProjectsV2Enabled] as const
};

const listProjects = async (
    isProjectsV2Enabled: boolean
): Promise<TProject[] | TV3Project[]> => {
    if (isProjectsV2Enabled) {
        return designerV2API.listProjects();
    }

    return projectsAPI.getProjects();
};

const getProject = async (
    isProjectsV2Enabled: boolean,
    projectId: string
): Promise<TProject | TV3Project> => {
    if (isProjectsV2Enabled) {
        return designerV2API.getProject(projectId);
    }

    return projectsAPI.getProject(projectId);
};

export function useProjectsQuery(
    isProjectsV2Enabled: true,
    options?: Omit<
        UseQueryOptions<TProject[] | TV3Project[]>,
        'queryKey' | 'queryFn'
    >
): UseQueryResult<TV3Project[]>;
export function useProjectsQuery(
    isProjectsV2Enabled?: false,
    options?: Omit<
        UseQueryOptions<TProject[] | TV3Project[]>,
        'queryKey' | 'queryFn'
    >
): UseQueryResult<TProject[]>;
export function useProjectsQuery(
    isProjectsV2Enabled = false,
    options?: Omit<
        UseQueryOptions<TProject[] | TV3Project[]>,
        'queryKey' | 'queryFn'
    >
): UseQueryResult<TProject[] | TV3Project[]> {
    return useQuery<TProject[] | TV3Project[]>({
        queryKey: PROJECT_QUERY_KEYS.list(isProjectsV2Enabled),
        queryFn: async () => listProjects(isProjectsV2Enabled),
        ...options
    });
}

export const useCreateProjectLegacyMutation = (
    options?: MutationOptions<TProjectData>
) => {
    return useMutation({
        ...options,
        mutationFn: async (data: TProjectData) =>
            projectsAPI.createProject(data)
    });
};

export const useUpdateProjectLegacyMutation = (
    options?: MutationOptions<{
        data: Partial<TProjectData>;
        projectId: string;
    }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({ data, projectId }) =>
            projectsAPI.updateProject(projectId, data)
    });
};

export const useDeleteProjectLegacyMutation = (
    options?: MutationOptions<{ projectId: string }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({ projectId }) =>
            projectsAPI.deleteProject(projectId)
    });
};

export const useProjectSearchQuery = (
    query?: string,
    filters?: {
        project_id?: string;
        agent_id?: string;
        flow_id?: string;
    },
    options?: Omit<UseQueryOptions<TSearchResult[]>, 'queryKey' | 'queryFn'>
) => {
    return useQuery({
        queryKey: ['projects', 'search', query, filters],
        queryFn: async () => projectsAPI.getProjectsSearch(query, filters),
        ...options
    });
};

export function useProjectQuery(
    isProjectsV2Enabled: true,
    projectId?: string,
    options?: Omit<
        UseQueryOptions<TProject | TV3Project>,
        'queryKey' | 'queryFn'
    >
): UseQueryResult<TV3Project>;
export function useProjectQuery(
    isProjectsV2Enabled: false,
    projectId?: string,
    options?: Omit<
        UseQueryOptions<TProject | TV3Project>,
        'queryKey' | 'queryFn'
    >
): UseQueryResult<TProject>;
export function useProjectQuery(
    isProjectsV2Enabled: boolean,
    projectId?: string,
    options?: Omit<
        UseQueryOptions<TProject | TV3Project>,
        'queryKey' | 'queryFn'
    >
): UseQueryResult<TProject | TV3Project>;
export function useProjectQuery(
    isProjectsV2Enabled: boolean,
    projectId?: string,
    options?: Omit<
        UseQueryOptions<TProject | TV3Project>,
        'queryKey' | 'queryFn'
    >
): UseQueryResult<TProject | TV3Project> {
    return useQuery<TProject | TV3Project>({
        queryKey: PROJECT_QUERY_KEYS.detail(isProjectsV2Enabled, projectId),
        queryFn: async () => {
            if (!projectId) {
                throw new Error('Project ID is required');
            }
            return getProject(isProjectsV2Enabled, projectId);
        },
        ...options,
        enabled: !!projectId && (options?.enabled ?? true)
    });
}

export const useCreateProjectMutation = (
    options?: MutationOptions<TV3CreateProjectData>
) => {
    return useMutation({
        ...options,
        mutationFn: async (data: TV3CreateProjectData) =>
            designerV2API.createProject(data)
    });
};

export const useUpdateProjectMutation = (
    options?: MutationOptions<{
        data: TV3UpdateProjectData;
        projectId: string;
    }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            data,
            projectId
        }: {
            data: TV3UpdateProjectData;
            projectId: string;
        }) => {
            return designerV2API.updateProject(projectId, data);
        }
    });
};

export const useDeleteProjectMutation = (
    options?: MutationOptions<{ projectId: string }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({ projectId }: { projectId: string }) => {
            return designerV2API.deleteProject(projectId);
        }
    });
};

export const useV3ForceUpdateProjectMutation = (
    options?: MutationOptions<{ projectIdn: string }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({ projectIdn }: { projectIdn: string }) => {
            const existing = await designerV2API.listCustomerLinks({
                project_idn: projectIdn
            });
            const link = existing.items[0];
            if (!link) {
                throw new Error(
                    'No active customer link for this project — cannot force-update'
                );
            }

            if (link.registry_idn && link.module_idn) {
                const registryLink: TRegistryCustomerLinkPayload = {
                    type: 'registry',
                    data: {
                        registry_idn: link.registry_idn,
                        module_idn: link.module_idn,
                        module_version: link.module_version
                    }
                };
                return designerV2API.upsertCustomerLink({
                    project_idn: projectIdn,
                    link: registryLink
                });
            }

            const localLink: TLocalCustomerLinkPayload = {
                type: 'local',
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                data: { project_version_id: link.project_version_id! }
            };
            return designerV2API.upsertCustomerLink({
                project_idn: projectIdn,
                link: localLink
            });
        }
    });
};

export const useForceUpdateProjectMutation = (
    options?: MutationOptions<{ projectId: string }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({ projectId }: { projectId: string }) => {
            return projectsAPI.forceUpdateProject(projectId);
        }
    });
};

export const useCreateProjectImageMutation = (
    options?: MutationOptions<{ data: TProjectImageData; projectId: string }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            data,
            projectId
        }: {
            data: TProjectImageData;
            projectId: string;
        }) => {
            return projectsAPI.makeProjectImage(projectId, data);
        }
    });
};

export const usePublishProjectMutation = (
    options?: MutationOptions<{
        projectId?: string;
        data: TProjectPublishData;
    }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            projectId,
            data
        }: {
            projectId?: string;
            data: TProjectPublishData;
        }) => {
            if (!projectId) {
                throw new Error('Project ID is required');
            }
            return projectsAPI.publishProject(projectId, data);
        }
    });
};
